import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Button,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { getWorkflowById } from "../../api";

const ViewWorkflowPage = () => {
  const { id } = useParams(); // Workflow ID from the URL
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchWorkflow();
  }, []);

  const fetchWorkflow = async () => {
    try {
      const response = await getWorkflowById(id); // Fetch workflow by ID
      setWorkflow(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching workflow:", error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!workflow) {
    return (
      <Box sx={{ padding: 4 }}>
        <Typography variant="h6" color="error">
          Workflow not found.
        </Typography>
      </Box>
    );
  }

  const renderAssets = () => {
    if (workflow.type === "global") {
      return (
        <Typography>
          Asset Type: <strong>{workflow.assetType}</strong>
        </Typography>
      );
    }

    if (workflow.type === "local" && workflow.assets?.length > 0) {
      return (
        <List>
          {workflow.assets.map((asset) => (
            <ListItem key={asset.assetId}>
              <ListItemText
                primary={`${asset.assetType} (ID: ${asset.assetId})`}
                secondary={asset.assetId["Location Name"] || "Location not specified"}
              />
            </ListItem>
          ))}
        </List>
      );
    }

    return <Typography>No assets associated with this workflow.</Typography>;
  };

  const renderEvents = () => {
    if (workflow.events?.length > 0) {
      return (
        <List>
          {workflow.events.map((event) => (
            <ListItem key={event._id}>
              <ListItemText
                primary={`Event: ${event.type}`}
                secondary={event.description || "No description available"}
              />
            </ListItem>
          ))}
        </List>
      );
    }
    return <Typography>No events associated with this workflow.</Typography>;
  };

  const renderActions = () => {
    if (workflow.actions?.length > 0) {
      return (
        <List>
          {workflow.actions.map((action) => (
            <ListItem key={action._id}>
              <ListItemText
                primary={`Action: ${action.name}`}
                secondary={
                  action.details.phoneNumber
                    ? `Phone: ${action.details.phoneNumber}`
                    : action.details.emailAddress
                    ? `Email: ${action.details.emailAddress}`
                    : "No contact info"
                }
              />
            </ListItem>
          ))}
        </List>
      );
    }
    return <Typography>No actions associated with this workflow.</Typography>;
  };

  return (
    <Box sx={{ padding: 4 }}>
      <Typography variant="h4" gutterBottom>
        View Workflow
      </Typography>
      <Paper sx={{ padding: 3, marginBottom: 2 }}>
        <Typography variant="h6">Workflow Information</Typography>
        <Divider sx={{ marginY: 2 }} />
        <Typography>Name: {workflow.name}</Typography>
        <Typography>Description: {workflow.description}</Typography>
        <Typography>Type: {workflow.type}</Typography>
      </Paper>

      <Paper sx={{ padding: 3, marginBottom: 2 }}>
        <Typography variant="h6">Assets</Typography>
        <Divider sx={{ marginY: 2 }} />
        {renderAssets()}
      </Paper>

      <Paper sx={{ padding: 3, marginBottom: 2 }}>
        <Typography variant="h6">Events</Typography>
        <Divider sx={{ marginY: 2 }} />
        {renderEvents()}
      </Paper>

      <Paper sx={{ padding: 3, marginBottom: 2 }}>
        <Typography variant="h6">Actions</Typography>
        <Divider sx={{ marginY: 2 }} />
        {renderActions()}
      </Paper>

      <Box sx={{ marginTop: 2 }}>
        <Button variant="contained" onClick={() => navigate("/workflow")}>
          Back to Workflows
        </Button>
      </Box>
    </Box>
  );
};

export default ViewWorkflowPage;
